// onlineMatch.js
// Client side of Bloom & Blight guest multiplayer. Guests are Firebase
// anonymous-auth users; every move goes through the bloomBlight* callables
// (server-validated by the same rules engine), and clients only *listen* to
// BloomBlightMatches. The subscription assembles a view state in which YOU
// are always player 0, so the battle UI renders both solo and online matches
// identically.
//
// Local end-to-end testing: open /game?emu=1 with `firebase emulators:start
// --only functions,firestore,auth` running.

import { doc, onSnapshot, connectFirestoreEmulator } from "firebase/firestore";
import { signInAnonymously, connectAuthEmulator } from "firebase/auth";
import {
  getFunctions,
  httpsCallable,
  connectFunctionsEmulator,
} from "firebase/functions";
import db, { auth } from "../../Firebase";

const fns = getFunctions(auth.app);

const useEmulators =
  typeof window !== "undefined" &&
  ["localhost", "127.0.0.1"].includes(window.location.hostname) &&
  new URLSearchParams(window.location.search).has("emu");
if (useEmulators && !window.__bbEmulatorsWired) {
  window.__bbEmulatorsWired = true;
  connectAuthEmulator(auth, "http://localhost:9099", { disableWarnings: true });
  connectFirestoreEmulator(db, "localhost", 8080);
  connectFunctionsEmulator(fns, "localhost", 5001);
}

const MATCHES = "BloomBlightMatches";

export const ensureGuest = async () => {
  if (auth.currentUser) return auth.currentUser;
  const cred = await signInAnonymously(auth);
  return cred.user;
};

const call = (name) => httpsCallable(fns, name);

export const createMatch = async (name, speciesIds) =>
  (await call("bloomBlightCreateMatch")({ name, speciesIds })).data;

export const joinMatch = async (joinCode, name, speciesIds) =>
  (await call("bloomBlightJoinMatch")({ joinCode, name, speciesIds })).data;

export const sendAction = async (matchId, action) =>
  (await call("bloomBlightPlayAction")({ matchId, action })).data;

// Reassemble the engine-shaped state from the public doc + my private doc,
// with a perspective swap so the local player is always index 0. Opponent
// hand/deck become placeholder arrays of the right length (the UI only shows
// their counts).
const buildView = (pubState, priv, myIdx) => {
  const state = JSON.parse(JSON.stringify(pubState));
  state.players = state.players.map((p, i) => ({
    ...p,
    hand:
      i === myIdx
        ? priv.hand
        : Array.from({ length: p.handCount }, (_, k) => ({ uid: `hidden-h${i}-${k}`, id: "hidden" })),
    deck:
      i === myIdx
        ? priv.deck
        : Array.from({ length: p.deckCount }, (_, k) => ({ uid: `hidden-d${i}-${k}`, id: "hidden" })),
  }));
  if (myIdx === 1) {
    state.players = [state.players[1], state.players[0]];
    state.active = 1 - state.active;
    if (state.winner !== null && state.winner !== undefined) state.winner = 1 - state.winner;
  }
  return state;
};

// Subscribe to a match. onUpdate receives either
//   { status: "waiting", joinCode }
// or
//   { status: "active" | "over", view, myIdx }
export const subscribeMatch = (matchId, uid, onUpdate, onError) => {
  let pub = null;
  let priv = null;
  const emit = () => {
    if (!pub) return;
    if (pub.status === "waiting") {
      onUpdate({ status: "waiting", joinCode: pub.joinCode });
      return;
    }
    if (!priv) return;
    const myIdx = pub.playerUids.indexOf(uid);
    if (myIdx === -1) {
      onError && onError(new Error("You're not part of this match."));
      return;
    }
    onUpdate({ status: pub.status, view: buildView(pub.state, priv, myIdx), myIdx });
  };
  const unsubPub = onSnapshot(
    doc(db, MATCHES, matchId),
    (snap) => { pub = snap.data(); emit(); },
    (err) => onError && onError(err)
  );
  const unsubPriv = onSnapshot(
    doc(db, MATCHES, matchId, "private", uid),
    (snap) => { priv = snap.data(); emit(); },
    (err) => onError && onError(err)
  );
  return () => { unsubPub(); unsubPriv(); };
};
